/**
 * WEEX WebSocket Configuration
 *
 * WebSocket channels, topology info, and low-level subscription utilities for WEEX.
 *
 * Subscribe format, channel names, and ping/pong mirror the production WEEX connector
 * (gitlab.1wq.net/mm/111-connman → connector/weex/market/ws/connector.go, common/{enum,helper}.go).
 *
 * @see wss://ws-spot.weex.com/v2/ws/public
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  WeexDepthWsMessage,
  WeexTickerWsMessage,
  WeexTradesWsMessage,
  WeexWsStatus,
  WeexWsSubscribeRequest,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const WEEX_WS_BASE_URL = 'wss://ws-spot.weex.com/v2/ws/public';

/**
 * WEEX public WS channel prefixes.
 * Channels are dot-joined: `ticker.<symbol>`, `depth.<symbol>.<level>`, `trades.<symbol>`.
 */
export const WEEX_WS_TICKER_PREFIX = 'ticker';
export const WEEX_WS_DEPTH_PREFIX = 'depth';
export const WEEX_WS_TRADES_PREFIX = 'trades';

/**
 * Order book depth level for WS subscriptions.
 * WEEX v2 public WS only supports levels 15 and 200.
 */
export const WEEX_WS_DEPTH_LEVEL = 15;

/**
 * WEEX WebSocket channel descriptors.
 * @see connector/weex/common/enum.go
 */
export const WEEX_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  orderbook: {
    channel: 'orderbook',
    topic: 'depth',
    supportsBatching: false,
    docUrl: 'https://api-spot.weex.com',
  },
  ticker: {
    channel: 'ticker',
    topic: 'ticker',
    supportsBatching: false,
    docUrl: 'https://api-spot.weex.com',
  },
  trades: {
    channel: 'trades',
    topic: 'trades',
    supportsBatching: false,
    docUrl: 'https://api-spot.weex.com',
  },
};

/**
 * WEEX WebSocket topology information.
 * WEEX v2 subscribes one channel per message and uses an application-level ping/pong.
 */
export const WEEX_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: WEEX_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 20_000,
  note: 'Subscribes one channel per message: {"event":"subscribe","channel":"ticker.BTCUSDT_SPBL"}. Server sends {"event":"ping","time":"..."}; reply with {"event":"pong","time":"..."}.',
};

// ========================================
// Channel builders
// ========================================

export const weexTickerChannel = (symbol: string): string =>
  `${WEEX_WS_TICKER_PREFIX}.${symbol}`;

export const weexDepthChannel = (symbol: string, level: number = WEEX_WS_DEPTH_LEVEL): string =>
  `${WEEX_WS_DEPTH_PREFIX}.${symbol}.${level}`;

export const weexTradesChannel = (symbol: string): string =>
  `${WEEX_WS_TRADES_PREFIX}.${symbol}`;

// ========================================
// WebSocket Subscription Functions
// ========================================

/**
 * Creates a WEEX WebSocket subscription for the given channel.
 * WEEX subscribes one channel per message and uses event-based ping/pong.
 */
function createWeexWsSubscription<T>(params: {
  channel: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { channel, onMessage, onError, onStatusChange } = params;

  const ws = new ReconnectingWebSocket(WEEX_WS_BASE_URL, [], {
    maxRetries: 10,
    minReconnectionDelay: 1000,
    maxReconnectionDelay: 30000,
    reconnectionDelayGrowFactor: 1.5,
    connectionTimeout: 10000,
  });

  ws.addEventListener('open', () => {
    onStatusChange?.('connected');

    // Send subscription message (one channel per message).
    const subscribeMessage: WeexWsSubscribeRequest = {
      event: 'subscribe',
      channel,
    };
    ws.send(JSON.stringify(subscribeMessage));
  });

  ws.addEventListener('close', () => {
    onStatusChange?.('disconnected');
  });

  ws.addEventListener('error', () => {
    onStatusChange?.('error');
    onError?.(new Error('WebSocket connection error'));
  });

  ws.addEventListener('message', event => {
    try {
      const message = JSON.parse(event.data);

      // Handle WEEX application-level ping: reply with a pong echoing the time.
      if (message.event === 'ping') {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ event: 'pong', time: message.time }));
        }
        return;
      }

      // Ignore our own pong / subscription acks.
      if (message.event === 'pong' || message.event === 'subscribe') {
        return;
      }

      // Forward data messages to handler.
      onMessage(message);
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Failed to parse message'));
    }
  });

  return ws;
}

/**
 * Subscribe to WEEX orderbook (depth) WebSocket.
 */
export function subscribeWeexOrderbook(params: {
  symbol: string;
  onMessage: (data: WeexDepthWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createWeexWsSubscription<WeexDepthWsMessage>({
    channel: weexDepthChannel(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to WEEX ticker WebSocket.
 */
export function subscribeWeexTicker(params: {
  symbol: string;
  onMessage: (data: WeexTickerWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createWeexWsSubscription<WeexTickerWsMessage>({
    channel: weexTickerChannel(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to WEEX public trades WebSocket.
 */
export function subscribeWeexTrades(params: {
  symbol: string;
  onMessage: (data: WeexTradesWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createWeexWsSubscription<WeexTradesWsMessage>({
    channel: weexTradesChannel(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}
